import React, { useState, useEffect, useCallback } from 'react';
import { formatDateTime, parseApiDate } from '../utils/dateUtils';
import {
  User,
  Users,
  Plus,
  Edit,
  Trash2,
  Key,
  Shield,
  UserCheck,
  UserX,
  Unlock,
  Clock,
  Filter,
  Moon,
  Sun,
  Smile,
  CreditCard,
  ChefHat,
  ShoppingCart,
  Apple,
  Flame,
  Image,
  MessageSquare,
  Globe,
  Camera,
  Video,
  ScanLine,
  Palette,
  Settings,
  Mail,
  Eye
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import DataTable, { Table } from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import ActionButton from '../components/ActionButton';
import BaseModal from '../components/BaseModal';
import Card from '../components/Card';
import FormField from '../components/FormField';
import ErrorAlert from '../components/ErrorAlert';
import FormActions from '../components/FormActions';
import OverviewStats from '../components/OverviewStats';

const UsersPage = () => {
  const { apiService } = useTheme();
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailUserId, setDetailUserId] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers();

      if (Array.isArray(response)) {
        setUsers(response);
      } else {
        setUsers(response.users || []);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [apiService]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Client-side filtering (backend returns all users)
  const filteredUsers = (users || []).filter(u => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const match = (u.username || '').toLowerCase().includes(term)
        || (u.full_name || '').toLowerCase().includes(term)
        || (u.email || '').toLowerCase().includes(term);
      if (!match) return false;
    }
    if (roleFilter && u.role !== roleFilter) return false;
    if (statusFilter && ((statusFilter === 'active') !== u.is_active)) return false;
    if (tierFilter && (u.subscription_tier || 'free') !== tierFilter) return false;
    return true;
  });

  const handleSort = (field) => {
    setSortDir(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc');
    setSortField(field);
  };

  const TIER_ORDER = { free: 0, plus: 1, pro: 2 };
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let av = a[sortField];
    let bv = b[sortField];
    if (sortField === 'created_at' || sortField === 'last_login' || sortField === 'last_seen_at') {
      av = av ? parseApiDate(av)?.getTime() ?? 0 : 0;
      bv = bv ? parseApiDate(bv)?.getTime() ?? 0 : 0;
    } else if (sortField === 'subscription_tier') {
      av = TIER_ORDER[av || 'free'] ?? 0;
      bv = TIER_ORDER[bv || 'free'] ?? 0;
    } else if (typeof av === 'string') {
      av = av.toLowerCase();
      bv = (bv || '').toLowerCase();
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const isNewUser = (user) => {
    if (!user.created_at) return false;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);
    const created = parseApiDate(user.created_at);
    return created !== null && created > cutoff;
  };

  useEffect(() => {
    const handlePasswordChange = (event) => {
      const { userId, username } = event.detail;
      let user;
      if (users && userId) {
        user = users.find(u => u.id === userId);
      } else if (users && username) {
        user = users.find(u => u.username === username);
      }
      if (user) {
        setSelectedUser(user);
        setShowPasswordModal(true);
      }
    };

    const handleUserProfile = (event) => {
      const { userId, username } = event.detail;
      let user;
      if (users && userId) {
        user = users.find(u => u.id === userId);
      } else if (users && username) {
        user = users.find(u => u.username === username);
      }
      if (user) {
        setSelectedUser(user);
        setIsOwnProfile(true);
        setShowEditModal(true);
      }
    };

    window.addEventListener('openPasswordChange', handlePasswordChange);
    window.addEventListener('openUserProfile', handleUserProfile);

    return () => {
      window.removeEventListener('openPasswordChange', handlePasswordChange);
      window.removeEventListener('openUserProfile', handleUserProfile);
    };
  }, [users]);

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowCreateModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsOwnProfile(false);
    setShowEditModal(true);
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await apiService.deleteUser(userId);
      loadUsers();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    }
  };

  const handleUnlockUser = async (userId) => {
    try {
      await apiService.unlockUser(userId);
      loadUsers();
    } catch (err) {
      console.error('Error unlocking user:', err);
      setError('Failed to unlock user');
    }
  };

  const handleRowClick = (user) => {
    setDetailUserId(user.id);
    setShowDetailModal(true);
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'free': return 'bg-gray-100 text-gray-700';
      case 'plus': return 'bg-emerald-100 text-emerald-800';
      case 'pro': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'Administrator': return <Shield className="w-4 h-4 text-red-500" />;
      case 'User': return <User className="w-4 h-4 text-blue-500" />;
      case 'Viewer': return <UserCheck className="w-4 h-4 text-green-500" />;
      default: return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusIcon = (user) => {
    if (user.locked_until && new Date(user.locked_until) > new Date()) {
      return <UserX className="w-4 h-4 text-red-500" />;
    }
    if (!user.is_active) {
      return <UserX className="w-4 h-4 text-orange-500" />;
    }
    return <UserCheck className="w-4 h-4 text-green-500" />;
  };

  const isUserLocked = (user) => {
    return user.locked_until && new Date(user.locked_until) > new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return formatDateTime(dateString);
  };

  const getStatusVariant = (role) => {
    switch (role) {
      case 'Administrator': return 'error';
      case 'User': return 'primary';
      case 'Viewer': return 'success';
      default: return 'secondary';
    }
  };

  const columns = [
    {
      header: 'ID',
      accessor: 'id',
      mobileHidden: true,
      sortable: true,
      render: (user) => (
        <span className="text-sm text-gray-500 font-mono">{user.id}</span>
      )
    },
    {
      header: 'User',
      accessor: 'username',
      sortable: true,
      render: (user) => (
        <div className="flex items-center">
          {getStatusIcon(user)}
          <div className="ml-3">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{user.username}</span>
              {isNewUser(user) && (
                <span className="px-1.5 py-0.5 text-xs font-semibold bg-green-100 text-green-700 rounded-full">New</span>
              )}
            </div>
            {user.full_name && <div className="text-sm text-gray-500">{user.full_name}</div>}
            {user.email && <div className="text-xs text-gray-400">{user.email}</div>}
          </div>
        </div>
      )
    },
    {
      header: 'Role',
      accessor: 'role',
      mobileHidden: true,
      render: (user) => (
        <div className="flex items-center space-x-2">
          {getRoleIcon(user.role)}
          <StatusBadge status={user.role} variant={getStatusVariant(user.role)} size="sm" />
        </div>
      )
    },
    {
      header: 'Tier',
      accessor: 'subscription_tier',
      sortable: true,
      render: (user) => {
        const t = user.subscription_tier || 'free';
        return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getTierBadgeColor(t)}`}>{t}</span>;
      }
    },
    {
      header: 'Status',
      accessor: 'status',
      mobileHidden: true,
      render: (user) => (
        <div className="flex flex-col space-y-1">
          {isUserLocked(user) ? (
            <StatusBadge status="Locked" variant="error" size="xs" />
          ) : user.is_active ? (
            <StatusBadge status="Active" variant="success" size="xs" />
          ) : (
            <StatusBadge status="Inactive" variant="warning" size="xs" />
          )}
          {user.failed_login_attempts > 0 && (
            <span className="text-xs text-orange-500">
              {user.failed_login_attempts} failed attempts
            </span>
          )}
        </div>
      )
    },
    {
      // last_seen_at = last authenticated request (stamped server-side,
      // throttled); falls back to last_login for rows predating the column
      header: 'Last Seen',
      accessor: 'last_seen_at',
      mobileHidden: true,
      sortable: true,
      render: (user) => (user.last_seen_at || user.last_login) ? (
        <div className="flex items-center space-x-1 text-sm text-gray-500">
          <Clock className="w-3 h-3" />
          <span>{formatDate(user.last_seen_at || user.last_login)}</span>
        </div>
      ) : (
        <span className="text-gray-400 text-sm">Never</span>
      )
    },
    {
      header: 'Joined',
      accessor: 'created_at',
      sortable: true,
      render: (user) => (
        <span className="text-sm text-gray-500">{formatDate(user.created_at)}</span>
      )
    },
    {
      header: 'Actions',
      accessor: 'actions',
      className: 'text-right',
      render: (user) => (
        <div className="flex justify-end space-x-1" onClick={(e) => e.stopPropagation()}>
          <ActionButton icon={Eye} onClick={() => { setDetailUserId(user.id); setShowDetailModal(true); }} variant="secondary" size="xs" />
          <ActionButton icon={Edit} onClick={() => handleEditUser(user)} variant="secondary" size="xs" className="hidden sm:inline-flex" />
          <ActionButton icon={Key} onClick={() => handleChangePassword(user)} variant="secondary" size="xs" className="hidden sm:inline-flex" />
          {isUserLocked(user) && (
            <ActionButton icon={Unlock} onClick={() => handleUnlockUser(user.id)} variant="secondary" size="xs" />
          )}
          <ActionButton icon={Trash2} onClick={() => setDeleteConfirm(user)} variant="danger" size="xs" className="hidden sm:inline-flex" />
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <OverviewStats
        show={showStats}
        stats={[
          { key: 'total-users', label: 'Total Users', value: users?.length || 0, icon: Users, iconColor: 'text-blue-500' },
          { key: 'active-users', label: 'Active Users', value: users?.filter(u => u.is_active)?.length || 0, icon: UserCheck, iconColor: 'text-green-500' },
          { key: 'locked-users', label: 'Locked Users', value: users?.filter(u => isUserLocked(u))?.length || 0, icon: UserX, iconColor: 'text-red-500' },
          { key: 'admin-users', label: 'Administrators', value: users?.filter(u => u.role === 'Administrator')?.length || 0, icon: Shield, iconColor: 'text-purple-500' }
        ]}
      />

      <DataTable
        title="User Management"
        subtitle="Manage system users, roles, and permissions"
        icon={Users}
        loading={loading}
        error={error}
        onRefresh={loadUsers}
        actions={[
          { icon: Plus, label: 'Create User', onClick: handleCreateUser, variant: 'primary' }
        ]}
        showStats={showStats}
        onToggleStats={() => setShowStats(!showStats)}
        searchProps={{
          value: searchTerm,
          onChange: (e) => setSearchTerm(e.target.value),
          placeholder: 'Search users...'
        }}
        filters={[
          {
            type: 'select',
            icon: Filter,
            value: roleFilter,
            onChange: (e) => setRoleFilter(e.target.value),
            options: [
              { value: '', label: 'All Roles' },
              { value: 'Administrator', label: 'Administrator' },
              { value: 'User', label: 'User' },
              { value: 'Viewer', label: 'Viewer' }
            ]
          },
          {
            type: 'select',
            icon: CreditCard,
            value: tierFilter,
            onChange: (e) => setTierFilter(e.target.value),
            options: [
              { value: '', label: 'All Tiers' },
              ...([...new Set((users || []).map(u => u.subscription_tier || 'free'))].sort().map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) })))
            ]
          },
          {
            type: 'select',
            icon: UserCheck,
            value: statusFilter,
            onChange: (e) => setStatusFilter(e.target.value),
            options: [
              { value: '', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' }
            ]
          }
        ]}
      >
        <Table
          columns={columns}
          data={sortedUsers}
          rowKey="id"
          onRowClick={handleRowClick}
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
        />

        {sortedUsers.length === 0 && !loading && (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600">Users will appear here once they are created.</p>
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} users
            </div>
            <div className="flex space-x-2">
              <ActionButton
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                variant="secondary"
                size="sm"
              >
                Previous
              </ActionButton>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {pagination.page} of {pagination.pages}
              </span>
              <ActionButton
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                variant="secondary"
                size="sm"
              >
                Next
              </ActionButton>
            </div>
          </div>
        )}
      </DataTable>

      {showDetailModal && detailUserId && (
        <UserDetailModal
          userId={detailUserId}
          apiService={apiService}
          onClose={() => {
            setShowDetailModal(false);
            setDetailUserId(null);
          }}
        />
      )}

      {deleteConfirm && (
        <BaseModal isOpen={true} onClose={() => setDeleteConfirm(null)} title="Delete User">
          <div className="p-6 space-y-4">
            <p className="text-gray-600">
              Are you sure you want to delete user <strong>{deleteConfirm.username}</strong>? This action cannot be undone.
            </p>
            <FormActions
              onCancel={() => setDeleteConfirm(null)}
              onSubmit={() => handleDeleteUser(deleteConfirm.id)}
              submitText="Delete"
              variant="danger"
            />
          </div>
        </BaseModal>
      )}

      {(showCreateModal || showEditModal) && (
        <UserModal
          user={selectedUser}
          isOwnProfile={isOwnProfile}
          apiService={apiService}
          onClose={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedUser(null);
            setIsOwnProfile(false);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedUser(null);
            setIsOwnProfile(false);
            loadUsers();
          }}
        />
      )}

      {showPasswordModal && selectedUser && (
        <PasswordChangeModal
          user={selectedUser}
          apiService={apiService}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
          }}
          onSave={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

const UserModal = ({ user, isOwnProfile = false, apiService, onClose, onSave }) => {
  const { theme, setTheme } = useTheme();
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    full_name: user?.full_name || '',
    password: '',
    confirm_password: '',
    role: user?.role || 'User',
    is_active: user?.is_active ?? true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user && formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const userData = {
        username: formData.username,
        email: formData.email || null,
        full_name: formData.full_name || null
      };

      if (!isOwnProfile) {
        userData.role = formData.role;
        userData.is_active = formData.is_active;
      }

      if (!user) {
        userData.password = formData.password;
        await apiService.createUser(userData);
      } else {
        await apiService.updateUser(user.id, userData);
      }

      onSave();
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.message || `Failed to ${user ? 'update' : 'create'} user`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal isOpen={true} onClose={onClose} title={user ? 'Edit User' : 'Create User'}>
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {error && <ErrorAlert error={error} variant="error" />}

        <FormField
          label="Username"
          type="text"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          required
          disabled={!!user}
          helpText={user ? "Username cannot be changed after creation" : undefined}
        />

        <FormField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />

        <FormField
          label="Full Name"
          type="text"
          value={formData.full_name}
          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
        />

        {!user && (
          <>
            <FormField
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={8}
              helpText="Must be 8+ characters with uppercase, lowercase, and number"
            />
            <FormField
              label="Confirm Password"
              type="password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              required
            />
          </>
        )}

        {!isOwnProfile && (
          <>
            <FormField
              label="Role"
              type="select"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              options={[
                { value: 'Viewer', label: 'Viewer' },
                { value: 'User', label: 'User' },
                { value: 'Administrator', label: 'Administrator' }
              ]}
            />
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Account is active
              </label>
            </div>
          </>
        )}

        {isOwnProfile && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Personal Settings</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Customize your personal preferences</p>

            <div className="flex items-center justify-between py-3">
              <div className="flex items-center space-x-3">
                {theme === 'dark' ? (
                  <Moon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                ) : (
                  <Sun className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                )}
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Theme</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Choose your preferred color scheme</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setTheme('light')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-colors text-sm ${
                    theme === 'light'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  Light
                </button>
                <button
                  type="button"
                  onClick={() => setTheme('dark')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-colors text-sm ${
                    theme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  Dark
                </button>
              </div>
            </div>
          </div>
        )}

        <FormActions
          onCancel={onClose}
          onSubmit={handleSubmit}
          submitText={user ? 'Update' : 'Create'}
          loading={loading}
          loadingText="Saving..."
        />
      </form>
    </BaseModal>
  );
};

const PasswordChangeModal = ({ user, apiService, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiService.changePassword(user.id, {
        current_password: formData.current_password,
        new_password: formData.new_password
      });

      onSave();
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal isOpen={true} onClose={onClose} title={`Change Password for ${user?.username || 'User'}`}>
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {error && <ErrorAlert error={error} variant="error" />}

        <input
          type="text"
          name="username"
          value={user?.username || ''}
          autoComplete="username"
          readOnly
          style={{ display: 'none' }}
          aria-hidden="true"
        />

        <FormField
          label="Current Password"
          type="password"
          value={formData.current_password}
          onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
          required
          autoComplete="current-password"
        />

        <FormField
          label="New Password"
          type="password"
          value={formData.new_password}
          onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
          required
          minLength={8}
          autoComplete="new-password"
          helpText="Must be 8+ characters with uppercase, lowercase, and number"
        />

        <FormField
          label="Confirm New Password"
          type="password"
          value={formData.confirm_password}
          onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
          required
          autoComplete="new-password"
        />

        <FormActions
          onCancel={onClose}
          onSubmit={handleSubmit}
          submitText="Change Password"
          loading={loading}
          loadingText="Changing..."
        />
      </form>
    </BaseModal>
  );
};

const UserDetailModal = ({ userId, apiService, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [tierSaving, setTierSaving] = useState(false);
  const [tierMessage, setTierMessage] = useState(null);
  const [creditsInput, setCreditsInput] = useState('');
  const [creditsSaving, setCreditsSaving] = useState(false);
  const [creditsMessage, setCreditsMessage] = useState(null);
  const [webAccessSaving, setWebAccessSaving] = useState(false);
  const [webAccessMessage, setWebAccessMessage] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const result = await apiService.getUser(userId);
        setData(result);
        setSelectedTier(result.subscription.tier);
        setCreditsInput(String(result.subscription.purchased_image_credits || 0));
      } catch (err) {
        setError(err.message || 'Failed to load user details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, apiService]);

  const handleApplyTier = async () => {
    if (selectedTier === data.subscription.tier) return;
    try {
      setTierSaving(true);
      setTierMessage(null);
      await apiService.patch(`/users/${userId}/subscription`, { tier: selectedTier });
      // Refresh data to get updated limits
      const result = await apiService.getUser(userId);
      setData(result);
      setSelectedTier(result.subscription.tier);
      setTierMessage(`Tier updated to ${result.subscription.tier}`);
    } catch (err) {
      setTierMessage(err.message || 'Failed to update tier');
    } finally {
      setTierSaving(false);
    }
  };

  const handleApplyCredits = async () => {
    const newCredits = parseInt(creditsInput, 10);
    if (isNaN(newCredits) || newCredits < 0) {
      setCreditsMessage('Enter a valid number (0 or more)');
      return;
    }
    if (newCredits === (data.subscription.purchased_image_credits || 0)) return;
    try {
      setCreditsSaving(true);
      setCreditsMessage(null);
      await apiService.patch(`/users/${userId}/subscription`, { purchased_image_credits: newCredits });
      const result = await apiService.getUser(userId);
      setData(result);
      setCreditsInput(String(result.subscription.purchased_image_credits || 0));
      setCreditsMessage(`Credits updated to ${result.subscription.purchased_image_credits}`);
    } catch (err) {
      setCreditsMessage(err.message || 'Failed to update credits');
    } finally {
      setCreditsSaving(false);
    }
  };

  const handleToggleWebAccess = async (grant) => {
    try {
      setWebAccessSaving(true);
      setWebAccessMessage(null);
      await apiService.patch(`/users/${userId}/web-access`, { web_access: grant });
      const result = await apiService.getUser(userId);
      setData(result);
      setWebAccessMessage(`Web access ${grant ? 'granted' : 'revoked'}`);
    } catch (err) {
      setWebAccessMessage(err.message || 'Failed to update web access');
    } finally {
      setWebAccessSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return formatDateTime(dateString);
  };

  const tierColors = {
    free: 'bg-gray-100 text-gray-700 border-gray-300',
    plus: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    pro: 'bg-amber-100 text-amber-800 border-amber-300',
  };

  const UsageBar = ({ label, icon: Icon, current, limit, limitKey }) => {
    const max = limit?.[limitKey] || 0;
    const pct = max > 0 ? Math.min((current / max) * 100, 100) : 0;
    const barColor = pct >= 100 ? 'bg-red-500' : pct >= 75 ? 'bg-yellow-500' : 'bg-green-500';
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center space-x-1.5 text-gray-600 dark:text-gray-400">
            <Icon className="w-3.5 h-3.5" />
            <span>{label}</span>
          </span>
          <span className="font-medium text-gray-900 dark:text-white">{current} / {max}</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div className={`${barColor} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  const StatBox = ({ label, value, icon: Icon, color = 'text-blue-500' }) => (
    <div className="text-center p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
      <Icon className={`w-5 h-5 mx-auto mb-1 ${color}`} />
      <div className="text-xl font-bold text-gray-900 dark:text-white">{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );

  const InfoRow = ({ label, value }) => (
    <div className="flex justify-between py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-white">{value ?? '—'}</span>
    </div>
  );

  return (
    <BaseModal isOpen={true} onClose={onClose} title="User Details" size="xl">
      <div className="p-6 space-y-5">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        )}
        {error && <ErrorAlert error={error} />}
        {data && (
          <>
            {/* Profile */}
            <Card padding="sm">
              <div className="flex items-start space-x-4">
                {data.profile.profile_picture_url ? (
                  <img src={data.profile.profile_picture_url} alt="" className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <User className="w-7 h-7 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                      {data.profile.full_name || data.profile.username}
                    </h3>
                    <StatusBadge
                      status={data.profile.role}
                      variant={data.profile.role === 'Administrator' ? 'error' : 'primary'}
                      size="xs"
                    />
                    <StatusBadge
                      status={data.profile.is_active ? 'Active' : 'Inactive'}
                      size="xs"
                    />
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">@{data.profile.username}</div>
                  {data.profile.email && (
                    <div className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      <Mail className="w-3.5 h-3.5" />
                      <span>{data.profile.email}</span>
                      {data.profile.email_verified && (
                        <UserCheck className="w-3.5 h-3.5 text-green-500" title="Verified" />
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 mt-4">
                <InfoRow label="Auth Provider" value={data.profile.auth_provider} />
                <InfoRow label="Last Login" value={formatDate(data.profile.last_login)} />
                <InfoRow label="Last Seen" value={formatDate(data.profile.last_seen_at)} />
                <InfoRow label="Created" value={formatDate(data.profile.created_at)} />
                <InfoRow label="Password Changed" value={formatDate(data.profile.password_changed_at)} />
                <InfoRow label="Failed Logins" value={data.profile.failed_login_attempts} />
                <InfoRow label="Locked Until" value={data.profile.locked_until ? formatDate(data.profile.locked_until) : '—'} />
              </div>
            </Card>

            {/* Subscription */}
            <Card padding="sm">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center space-x-2">
                <CreditCard className="w-4 h-4" />
                <span>Subscription</span>
              </h4>

              {/* Tier toggle buttons */}
              {data.subscription.available_tiers && (
                <div className="mb-3">
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(data.subscription.available_tiers).map((t) => {
                      const isActive = t === data.subscription.tier;
                      const isSelected = t === selectedTier;
                      const colors = tierColors[t] || 'bg-gray-100 text-gray-800 border-gray-300';
                      return (
                        <button
                          key={t}
                          onClick={() => setSelectedTier(t)}
                          className={`px-4 py-1.5 rounded-full text-sm font-semibold border capitalize transition-all ${
                            isSelected
                              ? `${colors} ring-2 ring-offset-1 ring-blue-500`
                              : isActive
                                ? colors
                                : 'bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 hover:text-gray-600 dark:hover:text-gray-300'
                          }`}
                        >
                          {t}
                          {isActive && ' (current)'}
                        </button>
                      );
                    })}
                  </div>
                  {selectedTier !== data.subscription.tier && (
                    <div className="mt-2 flex items-center space-x-2">
                      <button
                        onClick={handleApplyTier}
                        disabled={tierSaving}
                        className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 transition-colors"
                      >
                        {tierSaving ? 'Applying...' : `Apply ${selectedTier}`}
                      </button>
                      <button
                        onClick={() => setSelectedTier(data.subscription.tier)}
                        className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {tierMessage && (
                    <p className={`mt-2 text-sm ${tierMessage.includes('updated') ? 'text-green-600' : 'text-red-600'}`}>
                      {tierMessage}
                    </p>
                  )}
                </div>
              )}

              {/* Image credits editor */}
              <div className="mb-3">
                <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Purchased Image Credits</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    value={creditsInput}
                    onChange={(e) => { setCreditsInput(e.target.value); setCreditsMessage(null); }}
                    className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {parseInt(creditsInput, 10) !== (data.subscription.purchased_image_credits || 0) && (
                    <button
                      onClick={handleApplyCredits}
                      disabled={creditsSaving}
                      className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 transition-colors"
                    >
                      {creditsSaving ? 'Saving...' : 'Apply'}
                    </button>
                  )}
                </div>
                {creditsMessage && (
                  <p className={`mt-1 text-sm ${creditsMessage.includes('updated') ? 'text-green-600' : 'text-red-600'}`}>
                    {creditsMessage}
                  </p>
                )}
              </div>

              {/* Web Access */}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Web Access (foozenie.com)</p>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${data.profile.web_access ? 'text-green-600' : 'text-gray-400'}`}>
                    {data.profile.web_access ? 'Granted' : 'Restricted'}
                  </span>
                  {data.profile.web_access ? (
                    <button
                      onClick={() => handleToggleWebAccess(false)}
                      disabled={webAccessSaving}
                      className="px-3 py-1 text-xs rounded-md bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
                    >
                      {webAccessSaving ? 'Saving...' : 'Revoke'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleToggleWebAccess(true)}
                      disabled={webAccessSaving}
                      className="px-3 py-1 text-xs rounded-md bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50"
                    >
                      {webAccessSaving ? 'Saving...' : 'Grant'}
                    </button>
                  )}
                </div>
                {webAccessMessage && (
                  <p className={`mt-1 text-sm ${webAccessMessage.includes('granted') || webAccessMessage.includes('revoked') ? 'text-green-600' : 'text-red-600'}`}>
                    {webAccessMessage}
                  </p>
                )}
              </div>

              {/* Show limits for selected tier */}
              {(() => {
                const displayLimits = (selectedTier && data.subscription.available_tiers?.[selectedTier]) || data.subscription.limits;
                return displayLimits && (
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <InfoRow label="Daily Scan" value={displayLimits.daily_scan_recipe} />
                    <InfoRow label="Daily URL" value={displayLimits.daily_from_url} />
                    <InfoRow label="Daily Photo" value={displayLimits.daily_from_photo} />
                    <InfoRow label="Daily Video" value={displayLimits.daily_from_video} />
                    <InfoRow label="Daily Chat" value={displayLimits.daily_from_chat} />
                    <InfoRow label="Chat Sessions" value={displayLimits.daily_chat_sessions} />
                    <InfoRow label="Chat Turns" value={displayLimits.max_chat_turns} />
                    <InfoRow label="Monthly Images" value={displayLimits.monthly_image_generations} />
                    <InfoRow label="Max Recipes" value={displayLimits.max_recipes} />
                    <InfoRow label="Max Pantry" value={displayLimits.max_pantry_items} />
                    <InfoRow label="Max Lists" value={displayLimits.max_shopping_lists} />
                    <InfoRow label="Combined Imports" value={displayLimits.daily_combined_imports || 'Unlimited'} />
                  </div>
                );
              })()}
            </Card>

            {/* Today's Usage */}
            {Object.keys(data.usage.today).length > 0 && (
              <Card padding="sm">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>Today's Usage</span>
                </h4>
                <div className="space-y-2.5">
                  <UsageBar label="Scan Recipe" icon={ScanLine} current={data.usage.today.scan_recipe} limit={data.subscription.limits} limitKey="daily_scan_recipe" />
                  <UsageBar label="URL Imports" icon={Globe} current={data.usage.today.url_imports} limit={data.subscription.limits} limitKey="daily_from_url" />
                  <UsageBar label="From Photo" icon={Camera} current={data.usage.today.from_photo} limit={data.subscription.limits} limitKey="daily_from_photo" />
                  <UsageBar label="From Video" icon={Video} current={data.usage.today.from_video} limit={data.subscription.limits} limitKey="daily_from_video" />
                  <UsageBar label="From Chat" icon={MessageSquare} current={data.usage.today.from_chat} limit={data.subscription.limits} limitKey="daily_from_chat" />
                  <UsageBar label="Chat Sessions" icon={MessageSquare} current={data.usage.today.chat_sessions} limit={data.subscription.limits} limitKey="daily_chat_sessions" />
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <UsageBar label="Monthly Images" icon={Image} current={data.usage.monthly_image_generations} limit={data.subscription.limits} limitKey="monthly_image_generations" />
                </div>
              </Card>
            )}

            {/* Content Counts */}
            {Object.keys(data.content).length > 0 && (
              <Card padding="sm">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center space-x-2">
                  <ChefHat className="w-4 h-4" />
                  <span>Content</span>
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  <StatBox label="Recipes" value={data.content.recipes_total} icon={ChefHat} color="text-orange-500" />
                  <StatBox label="Public" value={data.content.recipes_public} icon={Globe} color="text-green-500" />
                  <StatBox label="Favorites" value={data.content.recipes_favorite} icon={Smile} color="text-pink-500" />
                  <StatBox label="Lists" value={data.content.shopping_lists} icon={ShoppingCart} color="text-blue-500" />
                  <StatBox label="Pantry" value={data.content.pantry_items} icon={Apple} color="text-emerald-500" />
                  <StatBox label="Spices" value={data.content.spices} icon={Flame} color="text-red-500" />
                </div>
              </Card>
            )}

            {/* Settings */}
            {data.settings && (
              <Card padding="sm">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center space-x-2">
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </h4>
                <div className="grid grid-cols-2 gap-x-6">
                  <InfoRow label="Theme" value={data.settings.theme} />
                  <InfoRow label="Theme Color" value={data.settings.theme_color} />
                  <InfoRow label="Filter Style" value={data.settings.filter_style} />
                  <InfoRow label="Default View" value={data.settings.default_view} />
                  <InfoRow label="Voice" value={data.settings.voice_name} />
                  <InfoRow label="Voice Locale" value={data.settings.voice_locale} />
                  {data.settings.dietary_preferences && data.settings.dietary_preferences.length > 0 && (
                    <div className="col-span-2 py-1.5">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Dietary Preferences</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {data.settings.dietary_preferences.map((pref) => (
                          <span key={pref} className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full border border-green-200">
                            {pref}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default UsersPage;
